<script setup lang="ts">
import {computed, provide, Ref, ref, watch, watchEffect} from "vue";
import Translator from "@/utils/common/Translator.ts";
import {GetRowIdParams, GridReadyEvent, PaginationChangedEvent} from "ag-grid-community";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import ListViewUtils from "@/utils/common/ListViewUtils.ts";
import ListViewToolbar from "@/components/listView/ListViewToolbar.vue";
import {useFilterGroupFixed} from "@/stores/nama-flags-store.ts";
import {useMessagesStore} from "@/stores/ToastDataStore.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import {QSelect} from "quasar";
import AgShowFieldInfoContextMenu from "@/utils/common/AgShowFieldInfoContextMenu.ts";
import CriteriaView from "@/views/CriteriaView.vue";
import {AgGridVue} from "ag-grid-vue3";
import EditViewListPresenter from "@/utils/common/EditViewListPresenter.ts";
import QuestionFieldsEditor from "@/components/questionFields/QuestionFieldsEditor.vue";
import {BlockProps, presenterKey} from "@/utils/presenters/interfaces.ts";
import {ColumnVisibleEvent, RowSelectionOptions} from "ag-grid-enterprise";
import FieldWidgetUtil from "@/utils/editView/widgets/FieldWidgetUtil.ts";
import NamaTimelineWrapper from "@/components/EditView/NamaTimelineWrapper.vue";
import {useNamaBreakpoints} from "@/composables/useNamaBreakpoints.ts";
import BusinessFunction from "@/utils/constants/BusinessFunction.ts";
import EnterIcon from "@/assets/editViewIcons/EnterIcon.vue";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import ExtraCriteriaWidgets from "@/views/ExtraCriteriaWidgets.vue";
import EditScreenList = namaTypes.EditScreenList;
import DTOTimelineData = namaTypes.DTOTimelineData;
import ListScreenLayout = namaTypes.ListScreenLayout;

const props = defineProps<BlockProps>();
const {filterGroupFixed} = useFilterGroupFixed();
let block = props.block as EditScreenList;
const showFilterGroup = ref(filterGroupFixed.value);
const agShowFieldInfoContextMenu = new AgShowFieldInfoContextMenu()
const qSelectOfSortFields = ref<QSelect | null>(null);
const presenter = new EditViewListPresenter(props.presenter, block);
provide(presenterKey, presenter);
const sortFieldsValue = presenter.sortFieldsValue;
const gridContainerRef = ref<HTMLElement | null>(null);
const displayedSortOptions: Ref<Record<string, string>[]> = ref([]);
const sortDirectionValue = presenter.sortDirectionValue;
const criteriaTree = presenter.criteriaTree;
const isGridReady = ref(false);
const paginationPageSize = ref(25);
const paginationPageSizeSelector = ref([25, 50, 100, 250, 1000, 2500, 5000]);
const isHorizontal = ref(true)
const showTimeline = ref(false);
const timelineData = ref<DTOTimelineData[]>([])
const currentPage = ref(1)
const totalPages = ref(1)
const canFetchTimelineData = ref(true)
const {isXSmall} = useNamaBreakpoints()
const startRow = computed<number>(() => (currentPage.value - 1) * paginationPageSize.value + 1);
const endRow = computed<number>(() => (currentPage.value) * paginationPageSize.value)
const pageDetail = ref()
const isFilterEmpty = computed(() => ListViewUtils.isFilterEmpty(presenter.criteriaTree.value));

const emit = defineEmits<{
  (e: "onListScreenLayoutFetched", layout: ListScreenLayout): void
}>()

async function fetchTimelineData() {
  if (!canFetchTimelineData.value || !presenter.layout?.supportsTimeline)
    return;
  try {
    canFetchTimelineData.value = false;
    const list = await presenter.listDataForTimeline(currentPage.value - 1, paginationPageSize.value);
    if (!list)
      return;
    const totalItems = list.rowCount || 0;
    timelineData.value = list.rowData;
    totalPages.value = Math.ceil(totalItems / paginationPageSize.value)
    if (list.pageNumber !== undefined)
      currentPage.value = (list.pageNumber + 1);
    pageDetail.value = startRow.value + " " + Translator.translateId("to") + " " + Math.min(endRow.value, list.rowCount) + " " + Translator.translateId("from") + " " + list.rowCount
    paginationPageSize.value = list.pageSize || paginationPageSize.value;
  } finally {
    canFetchTimelineData.value = true;
  }
}

const rowSelection: RowSelectionOptions = {mode: "multiRow", checkboxes: true, headerCheckbox: true};
const cacheBlockSize = computed(() => {
  return paginationPageSize.value < 100 ? paginationPageSize.value * 2 : paginationPageSize.value
});

watchEffect(async () => {
  presenter.fetchFilledQuestionFields(presenter.layout?.questionFields ?? []);
  if (props.collapsed)
    return;
  if (!isGridReady.value) {
    await presenter.start();
    showTimeline.value = !!presenter.layout?.supportsTimeline
      && NamaCommon.getStoredValue(ListViewUtils.showTimelineStorageKey(presenter.layout?.id), !!presenter.layout?.showTimelineByDefault)
    isHorizontal.value = !!presenter.layout?.horizontalTimeline
    isGridReady.value = true;
    if (presenter.layout)
      emit("onListScreenLayoutFetched", presenter.layout);
  } else {
    if (!presenter.gridApi.value?.isDestroyed())
      presenter.gridApi.value?.refreshServerSide({purge: true});
  }
})

watch([showTimeline, currentPage], async () => {
  if (props.collapsed)
    return;
  if (showTimeline.value)
    await fetchTimelineData();
}, {immediate: true})

function onPageChanged(page: number) {
  if (!canFetchTimelineData.value)
    return;
  currentPage.value = page;
}

function toggleFilterGroup() {
  if (filterGroupFixed.value) {
    useMessagesStore().pushMessage({message: {arabicText: 'شريط الفلترة والترتيب مثبت', englishText: 'Filters Group Is Fixed'}, warning: true});
    return;
  }
  showFilterGroup.value = !showFilterGroup.value;
}

function onUpdateSortFields(newValue: Record<string, string>[]) {
  sortFieldsValue.value = newValue;
  presenter.fetchPlace().orderBy = StringUtils.toCSVLineWithSep(',', newValue.map(l => l.value));
  qSelectOfSortFields.value?.updateInputValue('', false);
  updateTimelineOrGridData();
}

function onFilterInSortOption(val, update) {
  update(() => ListViewUtils.filterSortOption(val, displayedSortOptions, presenter.sortOptions),
    ref => {
      if (val !== '' && ref.options.length > 0) {
        ref.setOptionIndex(-1)
        ref.moveOptionSelection(1, true)
      }
    })
}

function onUpdateDirectionFields(newValue: Record<string, string>) {
  sortDirectionValue.value = newValue;
  presenter.fetchPlace().ascending = newValue.value === ListViewUtils.ASC;
  updateTimelineOrGridData();
}

function toggleSortDirection() {
  let newDir = ListViewUtils.ascSortDir();
  if (sortDirectionValue.value.value === ListViewUtils.ascSortDir().value)
    newDir = ListViewUtils.descSortDir();
  onUpdateDirectionFields(newDir);
}

async function onCriteriaViewEnterDown() {
  await FieldWidgetUtil.saveEditingFieldsAndGrids();
  updateTimelineOrGridData()
}

function updateTimelineOrGridData() {
  if (showTimeline.value)
    fetchTimelineData()
  else if (!presenter.gridApi.value?.isDestroyed())
    presenter.gridApi.value?.refreshServerSide({purge: true});
}

function onGridReady(params: GridReadyEvent) {
  presenter.gridApi.value = params.api;
  presenter.gridApi.value!.setGridOption("serverSideDatasource", presenter.createServerSideDatasource());
}

function onPaginationChanged(event: PaginationChangedEvent) {
  if (event.newPageSize)
    paginationPageSize.value = event.api.paginationGetPageSize();
}

function onVisibilityChanged(event: ColumnVisibleEvent) {
  if (!event.visible || !presenter.gridApi.value)
    return;
  presenter.gridApi.value.refreshServerSide();
}

function refreshList(){
  if (!presenter.gridApi.value?.isDestroyed())
    presenter.gridApi.value?.refreshServerSide();
}

defineExpose({refreshList})
</script>

<template>
  <template v-if="!collapsed">
    <div :data-ag-block-id="block.id"
         class="tw-bg-namaBackground-deep-light dark:tw-bg-namaBackground-deep-dark tw-flex tw-flex-col tw-rounded-lg tw-h-full"
         ref="gridContainerRef">
      <div class="tw-shrink-0 tw-bg-namaBackground-dim-light dark:tw-bg-namaBackground-dim-dark tw-rounded-t-lg tw-py-1.5 tw-px-2.5 compact:tw-px-1.5 ultra:tw-px-0.5"
           ref="toolbarRef">
        <ListViewToolbar
          ref="toolbar"
          v-if="presenter.gridApi.value || showTimeline"
          :presenter="presenter"
          @refreshTimelineData="fetchTimelineData"
          @toggleFilterGroup="toggleFilterGroup"
          @toggleSortDirection="toggleSortDirection"
          :supportsTimeline="presenter.layout?.supportsTimeline"
          v-model:showTimeline="showTimeline"/>
      </div>
      <q-slide-transition>
        <div ref="filterGroupRef" class="tw-w-full tw-flex tw-flex-col tw-px-3 tw-pt-2 tw-my-2 tw-gap-2" v-show="showFilterGroup">
          <div v-if="isGridReady" class="tw-w-[90%] tw-flex tw-gap-[1%] mobile:tw-w-full tw-items-start mobile:tw-flex-col mobile:tw-gap-[5px]">
            <ExtraCriteriaWidgets :isListView="true" :withoutViewNames="true" :presenter="presenter" :sortDirections="ListViewUtils.sortDirections()"
                                  @updateGridData="refreshList"/>
          </div>
          <div class="tw-w-full">
            <QuestionFieldsEditor :presenter="presenter.questionFieldPresenter" v-if="showFilterGroup"
                                  :doNotApplyThemeLayouts="true"/>
          </div>
          <div class="tw-w-full tw-h-fit">
            <CriteriaView v-model:criteria-tree="criteriaTree" :owner-entity="presenter.entityType" :isWidget="false"
                            :isFilterEmpty="isFilterEmpty" :onCriteriaViewEnterDown="onCriteriaViewEnterDown"
                            :criteria-fields="presenter.availableCriteriaFields" ref="criteriaView" @enterDown="onCriteriaViewEnterDown"/>
          </div>
          <a v-if="!isFilterEmpty"
             class="tw-bg-primary-light hover:tw-opacity-90 active:tw-opacity-95 dark:tw-bg-primary-dark tw-p-2 tw-rounded-lg tw-cursor-pointer tw-w-[100px] mobile:tw-w-full tw-mr-5 mobile:tw-mr-0 tw-self-end"
             @click="onCriteriaViewEnterDown" :title="Translator.translateId(BusinessFunction.refresh)">
            <EnterIcon class="tw-size-4 mobile:tw-size-5 !tw-fill-white tw-justify-self-center"/>
          </a>
        </div>
      </q-slide-transition>
      <div v-if="showTimeline" class="tw-w-full tw-flex-grow tw-p-3 tw-pb-0 tw-flex tw-flex-col tw-overflow-y-auto">
        <NamaTimelineWrapper :timeline="timelineData" :isHorizontal="isHorizontal" supportSnakeVerticalLayout class="tw-flex-grow"/>
        <div class="tw-shrink-0 tw-items-center tw-flex mobile:tw-flex-col mobile:tw-justify-center tw-gap-2 tw-p-2 tw-border-b-[1px] dark:tw-border-white/20 tw-border-black/20">
          <q-pagination
            :model-value="currentPage"
            @update:model-value="onPageChanged"
            :max="totalPages"
            direction-links
            boundary-links
            boundary-numbers
            v-bind="{
              maxPages:isXSmall?3:6,
              size:isXSmall?'13px':'md',
              ellipses:!isXSmall
            }"
            color="grey-8"
            icon-first="skip_previous"
            icon-last="skip_next"
          />
          <span v-text="pageDetail" class="tw-text-gray-500 dark:tw-text-gray-400 tw-text-sm"/>
        </div>
      </div>
      <AgGridVue
        :popupParent="gridContainerRef"
        v-else-if="isGridReady"
        :getContextMenuItems="agShowFieldInfoContextMenu.getContextMenuItems.value"
        :getMainMenuItems="agShowFieldInfoContextMenu.getMainMenuItems.value"
        style="width: 100%; font-size: var(--ag-font-size);"
        class="tw-grow"
        rowModelType="serverSide"
        :columnDefs="presenter.columnDefs"
        :rowSelection="rowSelection"
        :cellSelection="true"
        :rowNumbers="true"
        :pagination="true"
        :paginationPageSize="paginationPageSize"
        :paginationPageSizeSelector="paginationPageSizeSelector"
        :cacheBlockSize="cacheBlockSize"
        :enableRtl="Translator.isLanguageRTL()"
        :localeText="Translator.fetchAGGridLocale()"
        :initialState="presenter.fetchInitialGridState()"
        :serverSideInitialRowCount="15000"
        :autoSizeStrategy='{type: "fitCellContents"}'
        :getRowId="(params: GetRowIdParams) => params.data.namaRowIndex"
        :sendToClipboard="AGGridUtils.sendToClipboard"
        @grid-ready="onGridReady"
        @state-updated="(params) => {ListViewUtils.persistGridState(presenter.fetchPlace(), params.state)}"
        @columnVisible="onVisibilityChanged"
        @paginationChanged="onPaginationChanged"
        :enableCellTextSelection="true"
        :processCellForClipboard="AGGridUtils.processCellForClipboard"
      ></AgGridVue>
    </div>
  </template>
</template>

<style scoped>

</style>
